import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function DeleteAccountButton({ className = "" }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!user || user.role === "admin") return null;

  const confirm = async () => {
    setLoading(true);
    try {
      const r = await api.delete("/users/me");
      toast.success(
        t("delacc.toast_success", {
          sims: r.data.deleted_simulations,
          files: r.data.deleted_storage_objects,
        }),
        { duration: 7000 },
      );
      await logout();
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("delacc.toast_error"));
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className={`text-zinc-500 hover:text-red-400 hover:bg-red-500/5 text-xs ${className}`}
        data-testid="delete-account-btn"
      >
        <Trash2 size={12} className="mr-1.5" />
        {t("delacc.btn")}
      </Button>
      <AlertDialog open={open} onOpenChange={(v) => !loading && setOpen(v)}>
        <AlertDialogContent
          className="bg-zinc-950 border-red-500/30"
          data-testid="delete-account-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {t("delacc.dialog_title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400 space-y-2">
              <span className="block">
                {t("delacc.dialog_intro")}{" "}
                <strong className="text-red-400">{t("delacc.dialog_intro_bold")}</strong>{" "}
                {t("delacc.dialog_intro_2")}
              </span>
              <span className="block">{t("delacc.dialog_deleted_label")}</span>
              <ul className="text-xs text-zinc-500 ml-4 space-y-0.5 list-disc">
                <li>{t("delacc.dialog_item_1")}</li>
                <li>{t("delacc.dialog_item_2")}</li>
                <li>{t("delacc.dialog_item_3")}</li>
              </ul>
              <span className="block text-[11px] text-zinc-600 italic">
                {t("delacc.dialog_footnote")}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={loading}
              className="bg-transparent border-white/15 text-zinc-300 hover:bg-white/5 hover:text-white"
              data-testid="delete-account-cancel"
            >
              {t("delacc.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirm}
              disabled={loading}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="delete-account-confirm"
            >
              {loading ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <Trash2 size={14} className="mr-1.5" />
              )}
              {t("delacc.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
